import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import {
	JackpotChipPng,
	VipSubscriptionBestValue,
	VipSubscriptionCrown,
	VipSubscriptionHeroLogo,
	VipSubscriptionSparkle,
	VipSubscriptionTopGlow,
} from '../../../../assets';
import Loader from '../../../../shared/Loader';
import { Package } from '../../../../store/types';
import { useDevice } from '../../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../../utils/hooks/useSafeViewport';
import GoldGradientText from '../../common/GoldGradientText';
import GradientPlate from '../../common/GradientPlate';
import { TouchableOpacity } from '../../common/Touchable';
import {
	benefits,
	FittedGoldLine,
	FONT,
	formatPrice,
	GoldLines,
	iconGlow,
	PRICE_PANEL_FILL,
	REWARD_CARD_FILL,
	rewards,
	SUBSCRIBE_FILL,
} from './vipClubShared';

interface VipScreenProps {
	vipPackages: Package[];
	onPayment: (pkg: Package) => void;
}

const DESIGN_WIDTH = 390;
// V3 keeps its blocks 15 in from the 390 frame's edges (360 wide).
const DESIGN_SIDE_MARGIN = 15;

/**
 * The numbers the bottom of the page is laid out with. The price panel is designed 360 wide and
 * the reward cards 87 x 134, so each scales from its own design width to what the page gives it.
 */
const getPanelLayout = (scale: number, frameWidth: number) => {
	const s = (value: number) => Math.round(value * scale * 10) / 10;
	const contentWidth = frameWidth - s(DESIGN_SIDE_MARGIN * 2);
	const rewardCardWidth = contentWidth / 4;
	const panelScale = contentWidth / 360;
	const cardScale = rewardCardWidth / 87;
	return {
		s,
		contentWidth,
		rewardCardWidth,
		rewardCardHeight: 216 * cardScale,
		p: (value: number) => value * panelScale,
		c: (value: number) => value * cardScale,
	};
};

const VipScreen: React.FC<VipScreenProps> = ({ vipPackages, onPayment }) => {
	const { t } = useTranslation();
	const { isWeb } = useDevice();
	const { width } = useSafeViewport();
	const isLoading = vipPackages.length === 0;
	const featuredPackage = vipPackages[0];
	// The redesigned VIP Club uses the same compact composition on web and native.
	const webFrameWidth = width > DESIGN_WIDTH ? width * 0.5 : width;
	const frameWidth = isWeb
		? Math.max(280, webFrameWidth)
		: Math.min(DESIGN_WIDTH, Math.max(280, width));
	const scale = isWeb ? frameWidth / DESIGN_WIDTH : Math.min(frameWidth / DESIGN_WIDTH, 1);
	const styles = createStyles(scale, frameWidth, true);
	const layout = getPanelLayout(scale, frameWidth);
	const priceText = formatPrice(featuredPackage);

	return (
		<View style={styles.root}>
			{isLoading ? (
				<View style={styles.loaderSpace}>
					<Loader />
				</View>
			) : (
				<View style={styles.panel}>
					{/* Only the glow is clipped to the panel's corners, so the price panel's own glow at the
					    bottom can spill past the panel without the panel padding room for it. */}
					<View pointerEvents='none' style={styles.topGlowClip}>
						<Image source={VipSubscriptionTopGlow} style={styles.topGlow} resizeMode='cover' />
					</View>

					{/* Figma V3: the logo 282 x 128, the headline 4 under it, the subhead at 158. */}
					<View style={styles.heroCard}>
						{/* The file fills the logo's box (cover), cropping a sliver off the top and bottom. */}
						<Image source={VipSubscriptionHeroLogo} style={styles.heroLogo} resizeMode='contain' />
						<View style={styles.heroHeadline}>
							<FittedGoldLine
								text={t('T_SITE_VIP_TAGLINE', {
									defaultValue: 'More rewards. More power. More fun.',
								})}
								maxWidth={layout.contentWidth}
								height={layout.s(29)}
								fontSize={layout.s(24)}
								align='center'
								shadow
							/>
						</View>
						<Text numberOfLines={1} adjustsFontSizeToFit style={styles.heroSubhead}>
							{t('T_SITE_VIP_BENEFITS_PLUS_REWARDS', {
								defaultValue: 'All the VIP benefits + exclusive subscriber rewards!',
							})}
						</Text>
					</View>

					<View style={styles.rewardsRow}>
						<GradientPlate
							width={layout.contentWidth}
							height={layout.rewardCardHeight}
							radius={layout.c(10)}
							stops={REWARD_CARD_FILL}
							stroke='#FFF908'
							strokeWidth={1}
						/>
						<View style={styles.rewardHeader}>
							<Image source={VipSubscriptionCrown} style={styles.rewardHeaderCrown} />
							<Image source={VipSubscriptionSparkle} style={styles.rewardHeaderSparkle} />
							<Text style={styles.rewardHeaderTitle}>
								{t('T_SITE_VIP_SUBSCRIBER_REWARDS', {
									defaultValue: 'EXCLUSIVE SUBSCRIBER REWARDS',
								})}
							</Text>
							<Image source={VipSubscriptionSparkle} style={styles.rewardHeaderSparkle} />
							<Image source={VipSubscriptionCrown} style={styles.rewardHeaderCrown} />
						</View>
						{rewards(t).map(([icon, title, description, amount], index) => (
							<View key={description} style={styles.rewardCard}>
								<Image source={icon} style={styles.rewardIcon} resizeMode='contain' />
								<View style={styles.rewardTitle}>
									<GoldLines
										lines={title}
										width={layout.c(85)}
										lineHeight={layout.c(14)}
										fontSize={layout.c(12)}
										fontWeight='600'
										align='center'
									/>
								</View>
								<View style={styles.rewardAmount}>
									<Image source={JackpotChipPng} style={styles.rewardChip} resizeMode='contain' />
									<Text style={styles.rewardAmountText}>{amount}</Text>
								</View>
								<Text style={styles.rewardDescription}>{description}</Text>
								{index > 0 ? <View style={styles.rewardDivider} /> : null}
							</View>
						))}
					</View>

					<SectionHeader
						label={t('T_SITE_VIP_ALL_BENEFITS', { defaultValue: 'ALL CURRENT VIP BENEFITS' })}
						styles={styles}
						second
					/>

					<View style={styles.benefitGrid}>
						{benefits(t).map(([icon, title, description]) => (
							<View key={description} style={styles.benefitCard}>
								<Image source={icon} style={styles.benefitIcon} resizeMode='contain' />
								<View style={styles.benefitTitle}>
									<GoldLines
										lines={title}
										width={layout.s(118)}
										lineHeight={layout.s(17)}
										fontSize={layout.s(14)}
										fontWeight='600'
									/>
								</View>
								<Text style={styles.benefitDescription}>{description}</Text>
							</View>
						))}
					</View>

					{/* The subscription purchase panel is a native-mobile-only surface. */}
					{!isWeb ? (
						<View style={styles.pricePanel}>
							<GradientPlate
								width={layout.contentWidth}
								height={layout.p(88)}
								radius={layout.p(12)}
								stops={PRICE_PANEL_FILL}
								stroke='#FFF908'
								strokeWidth={1.1}
							/>

							<View style={styles.planName}>
								{[
									t('T_SITE_VIP_CLUB', { defaultValue: 'VIP CLUB' }),
									t('T_SITE_VIP_SUBSCRIPTION', { defaultValue: 'SUBSCRIPTION' }),
								].map(line => (
									<FittedGoldLine
										key={line}
										text={line}
										maxWidth={layout.p(80)}
										height={layout.p(14.4)}
										fontSize={layout.p(12)}
										letterSpacing={layout.p(0.48)}
									/>
								))}
							</View>
							<Text style={styles.billingNote}>
								{/* The design breaks the note after its first sentence. */}
								{t('T_SITE_VIP_CANCEL_ANYTIME', {
									defaultValue: 'Cancel anytime. Billed monthly.',
								}).replace('. ', '.\n')}
							</Text>

							<View style={styles.priceRow}>
								<GoldGradientText
									text={priceText}
									width={layout.p(22) * 0.56 * priceText.length + layout.p(4)}
									height={layout.p(28.6)}
									fontSize={layout.p(22)}
									align='left'
								/>
								<Text style={styles.period}>
									{t('T_SITE_VIP_PER_MONTH', { defaultValue: '/ month' })}
								</Text>
							</View>
							<TouchableOpacity
								onPress={() => onPayment(featuredPackage)}
								activeOpacity={0.85}
								style={styles.subscribeButton}
							>
								<GradientPlate
									width={layout.p(146)}
									height={layout.p(26)}
									radius={layout.p(13)}
									stops={SUBSCRIBE_FILL}
									stroke='rgba(184,255,140,0.75)'
									strokeWidth={1}
								/>
								<Text style={styles.subscribeText}>
									{t('T_SITE_VIP_SUBSCRIBE_NOW', { defaultValue: 'SUBSCRIBE NOW' })}
								</Text>
							</TouchableOpacity>

							<Text numberOfLines={1} adjustsFontSizeToFit style={styles.legalLinks}>
								<Text style={styles.legalLink}>
									{t('T_SITE_DATA_CONTROL_TERMS_LINK', { defaultValue: 'Terms of Service' })}
								</Text>
								{'  •  '}
								<Text style={styles.legalLink}>
									{t('T_SITE_DATA_CONTROL_PRIVACY_LINK', { defaultValue: 'Privacy Policy' })}
								</Text>
								{'  •  '}
								<Text style={styles.legalLink}>
									{t('T_SITE_VIP_RESTORE_PURCHASES', { defaultValue: 'Restore Purchases' })}
								</Text>
							</Text>

							<Image
								source={VipSubscriptionBestValue}
								style={styles.bestValueImage}
								resizeMode='contain'
							/>
						</View>
					) : null}
				</View>
			)}
		</View>
	);
};

// Figma V3 gives the first header 18 above / 13 below and the second 19 / 15.
const SectionHeader = ({
	label,
	styles,
	second = false,
	crowned = false,
}: {
	label: string;
	styles: ReturnType<typeof createStyles>;
	second?: boolean;
	crowned?: boolean;
}) => (
	<View style={[styles.sectionHeader, second && styles.sectionHeaderSecond]}>
		<View style={styles.headerRule} />
		{crowned ? <Image source={VipSubscriptionCrown} style={styles.sectionCrown} /> : null}
		<Text style={styles.sectionTitle}>{label}</Text>
		{crowned ? <Image source={VipSubscriptionCrown} style={styles.sectionCrown} /> : null}
		<View style={styles.headerRule} />
	</View>
);

const createStyles = (scale: number, frameWidth: number, isDesktopLayout: boolean) => {
	const s = (value: number) => Math.round(value * scale * 10) / 10;
	const font = (value: number, min: number) => Math.max(min, Math.round(value * scale));
	const { contentWidth, rewardCardWidth, rewardCardHeight, p, c } = getPanelLayout(
		scale,
		frameWidth,
	);
	const benefitGap = s(8);
	const benefitCardWidth = (contentWidth - benefitGap) / 2;

	return StyleSheet.create({
		root: {
			width: frameWidth,
			alignSelf: 'center',
		},
		panel: {
			width: frameWidth,
			alignSelf: 'center',
			paddingHorizontal: s(DESIGN_SIDE_MARGIN),
			paddingTop: s(isDesktopLayout ? 8 : 0),
			borderRadius: s(14),
		},
		topGlowClip: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(14),
			overflow: 'hidden',
		},
		topGlow: {
			position: 'absolute',
			top: s(-105),
			left: s(-41),
			width: s(473),
			height: s(684),
			opacity: 0.45,
		},
		// Keep the logo box at the asset's native aspect ratio so `contain` adds no vertical padding.
		heroCard: {
			width: contentWidth,
			height: s(268),
			alignItems: 'center',
		},
		heroLogo: {
			position: 'absolute',
			top: 0,
			width: contentWidth,
			height: s(218),
		},
		heroHeadline: {
			position: 'absolute',
			top: s(220),
		},
		heroSubhead: {
			position: 'absolute',
			top: s(246),
			width: contentWidth,
			fontFamily: FONT,
			fontSize: font(16, 12),
			lineHeight: font(16, 13),
			fontWeight: '600',
			color: '#fff',
			textAlign: 'center',
			textShadowColor: 'rgba(38,0,0,0.8)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(4),
		},
		// "Membership extras" (6202:33700): 360 x 112, four 90-wide perks, the plate drawn by a
		// GradientPlate. The fill under it only gives Android's elevation shadow something to cast.
		featureRow: {
			width: contentWidth,
			height: s(112),
			borderRadius: s(10),
			backgroundColor: '#2E0802',
			flexDirection: 'row',
			shadowColor: '#ff7200',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.9,
			shadowRadius: s(14),
			elevation: 8,
		},
		featureItem: {
			width: contentWidth / 4,
			height: s(112),
			alignItems: 'center',
			position: 'relative',
		},
		// Icon 48 x 45.5 at 13, label (DS/Label/M, SemiBold 12 / 1.2) at 66.5.
		featureIcon: {
			position: 'absolute',
			top: s(13),
			width: s(48),
			height: s(45.5),
		},
		featureLabel: {
			position: 'absolute',
			top: s(66.5),
		},
		featureDivider: {
			position: 'absolute',
			left: 0,
			top: s(16),
			width: 1,
			height: s(80),
			backgroundColor: 'rgba(255,204,89,0.18)',
		},
		// Rules from 15 in from the frame edges to the label; Label/Overline (Black 14, 8% tracking).
		sectionHeader: {
			width: frameWidth,
			height: s(20),
			marginLeft: s(-DESIGN_SIDE_MARGIN),
			marginTop: s(10),
			marginBottom: s(10),
			paddingHorizontal: s(15),
			flexDirection: 'row',
			alignItems: 'center',
		},
		sectionHeaderSecond: {
			marginTop: s(12),
			marginBottom: s(10),
		},
		headerRule: {
			flex: 1,
			height: 1,
			backgroundColor: 'rgba(255,204,77,0.68)',
		},
		sectionTitle: {
			fontFamily: FONT,
			paddingHorizontal: s(7),
			fontSize: font(14, 10),
			lineHeight: font(20, 13),
			fontWeight: '900',
			letterSpacing: s(1.12),
			color: '#ffd85a',
			textAlign: 'center',
		},
		sectionCrown: {
			width: s(18),
			height: s(17),
			marginHorizontal: s(2),
			resizeMode: 'contain',
		},
		benefitGrid: {
			width: contentWidth,
			flexDirection: 'row',
			flexWrap: 'wrap',
			gap: benefitGap,
		},
		// "Benefit" (176 x 86): icon 36 x 34 at 7, 8; title from 51, 9 (Condensed SemiBold 14 / 17,
		// up to two lines); description (Medium 10 / 12) at a fixed 50 whatever the title's length.
		// The file wraps "Change your name / anytime", so the copy box is 117 wide.
		benefitCard: {
			width: benefitCardWidth,
			height: s(86),
			borderWidth: 1.2,
			borderColor: '#da600e',
			borderRadius: s(9),
			backgroundColor: '#330e07',
			shadowColor: '#ffb21a',
			shadowOpacity: 0.55,
			shadowRadius: s(3),
		},
		benefitIcon: {
			position: 'absolute',
			left: s(7),
			top: s(8),
			width: s(36),
			height: s(34),
			...iconGlow(s(5)),
		},
		benefitTitle: {
			position: 'absolute',
			left: s(51),
			top: s(9),
		},
		benefitDescription: {
			position: 'absolute',
			left: s(51),
			top: s(50),
			width: s(117),
			fontFamily: FONT,
			fontSize: s(10),
			lineHeight: s(12),
			fontWeight: '500',
			color: '#ebe3d6',
		},
		rewardsRow: {
			width: contentWidth,
			height: rewardCardHeight,
			position: 'relative',
			flexDirection: 'row',
			borderRadius: c(10),
			shadowColor: '#ff730d',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.4,
			shadowRadius: c(6),
		},
		rewardHeader: {
			position: 'absolute',
			top: c(13),
			width: '100%',
			height: c(22),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
		},
		rewardHeaderSparkle: {
			width: c(15),
			height: c(15),
			marginHorizontal: c(3),
			resizeMode: 'contain',
		},
		rewardHeaderCrown: {
			width: c(19),
			height: c(18),
			marginHorizontal: c(1),
			resizeMode: 'contain',
		},
		rewardHeaderTitle: {
			fontFamily: FONT,
			fontSize: c(14),
			lineHeight: c(18),
			fontWeight: '900',
			letterSpacing: c(0.7),
			color: '#FFD24B',
			textAlign: 'center',
		},
		// The new reward card carries its art, chip total and reward detail vertically.
		rewardCard: {
			width: rewardCardWidth,
			height: c(165),
			marginTop: c(43),
		},
		rewardIcon: {
			position: 'absolute',
			left: c(15),
			top: c(2),
			width: c(57),
			height: c(57),
			...iconGlow(c(5)),
		},
		rewardTitle: {
			position: 'absolute',
			left: c(1),
			top: c(62),
		},
		rewardDescription: {
			position: 'absolute',
			left: c(1),
			top: c(112),
			width: c(85),
			fontFamily: FONT,
			fontSize: c(8),
			lineHeight: c(10),
			fontWeight: '500',
			color: '#EBE3D6',
			textAlign: 'center',
		},
		rewardAmount: {
			position: 'absolute',
			top: c(94),
			width: c(85),
			height: c(16),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
		},
		rewardChip: {
			width: c(14),
			height: c(14),
			marginRight: c(2),
		},
		rewardAmountText: {
			fontFamily: FONT,
			fontSize: c(13),
			lineHeight: c(16),
			fontWeight: '700',
			color: '#FFD24B',
		},
		rewardDivider: {
			position: 'absolute',
			left: 0,
			top: c(6),
			width: 1,
			height: c(148),
			backgroundColor: 'rgba(255,204,89,0.22)',
		},
		// Figma "Price Panel" (2583:1126). The plate draws the fill and the rim; the view carries
		// the orange glow around it.
		// 11 under the reward cards (V3: rewards end at 1020, the panel starts at 1031).
		pricePanel: {
			marginTop: s(11),
			width: contentWidth,
			height: p(88),
			borderRadius: p(12),
			// Under the plate; Android only casts an elevation shadow from a filled view.
			backgroundColor: '#1F0704',
			position: 'relative',
			shadowColor: '#ff730d',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.4,
			shadowRadius: p(12),
			elevation: 6,
		},
		// Label/Micro: Sofia Sans Condensed Black 12 / 1.2, 0.48 tracked, gold gradient.
		planName: {
			position: 'absolute',
			left: p(14),
			top: p(7),
		},
		// Label/Tiny: SemiBold 10 / 12.
		billingNote: {
			position: 'absolute',
			left: p(14),
			top: p(38),
			width: p(80),
			fontFamily: FONT,
			fontSize: p(10),
			lineHeight: p(12),
			fontWeight: '600',
			color: '#F2D16B',
		},
		// Heading/H2 price at 121, 4 and Label/Small "/ month" 5 after it, on the button's centre line
		// so a longer price still sits over it.
		priceRow: {
			position: 'absolute',
			left: p(99),
			top: p(4),
			width: p(146),
			flexDirection: 'row',
			justifyContent: 'center',
		},
		period: {
			marginLeft: p(5),
			marginTop: p(10),
			fontFamily: FONT,
			fontSize: p(12),
			lineHeight: p(14),
			fontWeight: '500',
			color: '#FFFFFF',
		},
		subscribeButton: {
			position: 'absolute',
			left: p(99),
			top: p(33),
			width: p(146),
			height: p(26),
			borderRadius: p(13),
			backgroundColor: '#248F1C',
			justifyContent: 'center',
			alignItems: 'center',
			shadowColor: '#40ff33',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: p(4.5),
			elevation: 4,
		},
		// Button/Label: Bold 14 / 14.
		subscribeText: {
			fontFamily: FONT,
			fontSize: p(14),
			lineHeight: p(14),
			fontWeight: '700',
			letterSpacing: p(0.055),
			color: '#FFFFFF',
			textAlign: 'center',
			textShadowColor: 'rgba(0,38,0,0.7)',
			textShadowOffset: { width: 0, height: p(1) },
			textShadowRadius: p(2),
		},
		// Caption/Tiny: Medium 10 / 10. The prototype's 238-wide box (2583:1315) keeps the three links
		// on one line inside the panel.
		legalLinks: {
			position: 'absolute',
			left: p(66),
			top: p(72),
			width: p(238),
			fontFamily: FONT,
			fontSize: p(10),
			lineHeight: p(10),
			fontWeight: '500',
			letterSpacing: p(0.02),
			color: '#F0E5D4',
			textAlign: 'center',
		},
		legalLink: {
			textDecorationLine: 'underline',
		},
		// "Chips + Best Value" (2583:1135): 96 x 66 at 262, 6.
		bestValueImage: {
			position: 'absolute',
			left: p(262),
			top: p(6),
			width: p(96),
			height: p(66),
		},
		loaderSpace: {
			minHeight: s(320),
			width: '100%',
			justifyContent: 'center',
			alignItems: 'center',
		},
	});
};

export default VipScreen;
